import React, { createContext, useState, useEffect, ReactNode } from 'react';
import cookieUtils from '@/utils/cookie'; // Mevcut cookie işlemleri için kullandığınız modül
import { useGetUserProfileQuery } from '@/components/shared/ApplicationForms/GeneralInformationForm/queries';

interface AuthState {
  user: API.CurrentUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error?: Error;
}

export interface AuthContextProps extends AuthState {
  login: (token: API.AuthTokenType, autoLogin?: boolean) => void;
  logout: () => void;
  refreshToken: () => void;
}

export const AuthContext = createContext<AuthContextProps | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<API.CurrentUser | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>();

  const { data: profile, isFetched: isProfileFetched } = useGetUserProfileQuery({
    enabled: isAuthenticated,
  });

  const fetchUserInfo = (): API.CurrentUser | null => {
    const token = cookieUtils.decodeAccessToken();
    if (!token) return null;

    return {
      id: token.sub!,
      name: token?.username?.includes('@') ? token.username.split('@')[0] : token.username!,
      given_name: token?.given_name,
      family_name: token?.family_name,
      access: (token?.role as string) || (token?.trole as { role: string }).role,
      email: token?.username?.includes('@') ? token.username : undefined,
      programId: token?.programId
    };
  };

  const login = (token: API.AuthTokenType, autoLogin = true) => {
    cookieUtils.setToken(token, autoLogin);
    const currentUser = fetchUserInfo();
    if (currentUser) {
      setUser(currentUser);
      setIsAuthenticated(true);
    }
  };

  const logout = () => {
    cookieUtils.removeLogoutCookies();
    setUser(null);
    setIsAuthenticated(false);
  };

  const refreshToken = () => {
    const refreshToken = cookieUtils.getRefreshToken();
    if (!refreshToken) {
      logout();
      return;
    }
    // Refresh token kodu burada çağrılabilir (örnek: bir endpoint'e istek atılır)
    const newToken = {
      /* Yeni bir token */
    } as API.AuthTokenType;
    login(newToken);
  };

  useEffect(() => {
    if (profile && isProfileFetched) {
      setUser({
        ...user!,
        given_name: profile.name,
        family_name: profile.surname,
        photo: `data:image/png;base64,${profile.profilePhotoBase64}`,
        unitId: profile.unitId,
        departmentId: profile.departmentId,
      });
    }
  }, [profile]);

  useEffect(() => {
    const initializeAuth = () => {
      try {
        const isValidToken = cookieUtils.checkTokenIfIsValid();
        if (isValidToken) {
          const currentUser = fetchUserInfo();
          setUser(currentUser);
          setIsAuthenticated(true);
        }
      } catch (err) {
        setError(err as Error);
        logout();
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();

    const interval = setInterval(() => {
      const isValidToken = cookieUtils.checkTokenIfIsValid();
      if (!isValidToken) {
        refreshToken();
      }
    }, 60000); // Her 60 saniyede token doğruluğunu kontrol et

    return () => clearInterval(interval);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isLoading,
        error,
        login,
        logout,
        refreshToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
